import { ElementSource, Story } from "chq";
import { IMAGE_TYPE } from "components/stories/video-studio/utils/constants";
import getTimeDifference from "./getTimeDifference";
import { findAllGenericElements } from "../../../genericTemplate/utils/findGenericElementByName";
import { SelectedMedia } from "../genericMontageTemplate";

interface ElementUpdate {
  id?: string;
  updates?: Partial<ElementSource>;
}

const getUpdatedTemplateElementsWithMedia = (
  elements: ElementSource[],
  onStoryUpdate: (story: Story) => void,
  selectedMedia: SelectedMedia[],
  oldElementsMappings: Record<string, { time: number; duration: number; trim_start: number }>
): ElementSource[] => {
  const { storyElement: storyElements } = findAllGenericElements(elements);
  const sortedStoryElements = [...storyElements].sort((a, b) => a.time - b.time);

  const storiesMap: ElementSource = selectedMedia.map((selectedVisual, index) => {
    const storyElement = sortedStoryElements[index];
    if (!storyElement) return {};

    return { [storyElement.id]: selectedVisual };
  }).reduce((acc, obj) => ({ ...acc, ...obj }), {});

  const firstStoryElement = sortedStoryElements.reduce((min, current) => (
    current.time < min.time ? current : min
  ), sortedStoryElements?.[0]);

  const timeDifference = getTimeDifference(sortedStoryElements);

  let currentTime = firstStoryElement.time;
  const elementsToDelete: string[] = [];
  const elementUpdates: ElementUpdate[] = [
    ...sortedStoryElements.map(element => {
      if (storiesMap[element.id]) {
        const { shareableUrl } = storiesMap[element.id].media;
        onStoryUpdate({ ...storiesMap[element.id], creatomateId: element.id });
        const duration = storiesMap[element.id].media.mediaType === IMAGE_TYPE
          ? 5
          : (oldElementsMappings[shareableUrl]?.duration ?? storiesMap[element.id].media.duration);
        const result = {
          id: element.id,
          updates: {
            time: currentTime + timeDifference[element.id],
            source: shareableUrl,
            type: storiesMap[element.id].media.mediaType,
            duration,
            trim_start: oldElementsMappings[shareableUrl]?.trim_start ?? 0
          }
        };
        currentTime += timeDifference[element.id] + duration;
        return result;
      }
      elementsToDelete.push(element.id);
      return {};
    })
  ];

  const elementMappings = elementUpdates
    .filter((update): update is ElementUpdate & { id: string } => Boolean(update.id))
    .reduce((acc, { id, updates }) => {
      acc[id] = { ...acc[id], ...updates };
      return acc;
    }, {} as Record<string, Partial<ElementSource>>);

  return elements.filter((element: ElementSource) => !elementsToDelete.includes(
    element.id
  )).map((element: ElementSource) => ({
    ...element,
    ...elementMappings[element.id]
  }));
};

export default getUpdatedTemplateElementsWithMedia;
